export function createPlayerInteractionSystem({
  controls,
  isUiWindowOpen,
  isPlayerDeathAnimating,
  getTargetHit,
  routeSecondaryInteraction,
  tryRepeatPlaceAction,
  onPrimarySwing,
  isMeleeAttackReady,
  tryAttackMob,
  onMeleeAttackSuccess,
  tryStartMining,
  isMining,
  tryPlaceActiveBlock,
  stopMining,
  stopShieldBlocking,
  isBowCharging,
  tryShootBowOnRelease,
  cancelBowCharge
} = {}) {
  const HOLD_PLACE_INITIAL_DELAY = 0.24;
  const HOLD_PLACE_REPEAT_INTERVAL = 0.145;
  const HOLD_PLACE_RETRY_INTERVAL = 0.095;
  const HOLD_MINE_RETRY_INTERVAL = 0.03;

  let primaryHeld = false;
  let secondaryHeld = false;
  let repeatPlaceArmed = false;
  let placeRepeatTimer = HOLD_PLACE_INITIAL_DELAY;
  let mineRetryTimer = 0;

  function readTargetHit(force = false) {
    return getTargetHit?.({ force }) || null;
  }

  function isGameplayInputBlocked() {
    return !controls?.isLocked || isUiWindowOpen?.() || isPlayerDeathAnimating?.();
  }

  function resetHeldState() {
    primaryHeld = false;
    secondaryHeld = false;
    repeatPlaceArmed = false;
    placeRepeatTimer = HOLD_PLACE_INITIAL_DELAY;
    mineRetryTimer = 0;
  }

  function handleWindowBlur() {
    resetHeldState();
  }

  function handleMouseDown(e) {
    if (isGameplayInputBlocked()) return;

    const hit = readTargetHit(true);
    if (e.button === 0) {
      primaryHeld = true;
      mineRetryTimer = HOLD_MINE_RETRY_INTERVAL;
      onPrimarySwing?.();

      if (isMeleeAttackReady?.() && tryAttackMob?.(hit)) {
        onMeleeAttackSuccess?.();
        stopMining?.();
        return;
      }

      tryStartMining?.(hit, { fromHold: false });
      return;
    }

    if (e.button !== 2) return;
    secondaryHeld = true;
    repeatPlaceArmed = false;
    placeRepeatTimer = HOLD_PLACE_INITIAL_DELAY;

    if (typeof routeSecondaryInteraction === 'function') {
      const routeResult = routeSecondaryInteraction(hit) || {};
      if (routeResult.stopMining) stopMining?.();
      repeatPlaceArmed = !!routeResult.armPlaceRepeat;
      return;
    }

    repeatPlaceArmed = true;
    tryPlaceActiveBlock?.(hit);
  }

  function handleMouseUp(e) {
    if (e.button === 0) {
      primaryHeld = false;
      mineRetryTimer = 0;
      stopMining?.();
    }
    if (e.button === 2) {
      secondaryHeld = false;
      repeatPlaceArmed = false;
      placeRepeatTimer = HOLD_PLACE_INITIAL_DELAY;
      stopShieldBlocking?.();
    }
    if (e.button === 2 && isBowCharging?.()) {
      tryShootBowOnRelease?.();
      cancelBowCharge?.();
    }
  }

  function update(delta = 0) {
    const safeDelta = Math.max(0, Number(delta) || 0);
    if (isGameplayInputBlocked()) {
      placeRepeatTimer = HOLD_PLACE_INITIAL_DELAY;
      mineRetryTimer = 0;
      return;
    }

    if (primaryHeld && !isMining?.()) {
      mineRetryTimer -= safeDelta;
      if (mineRetryTimer <= 0) {
        const hit = readTargetHit(false);
        tryStartMining?.(hit, { fromHold: true });
        mineRetryTimer = HOLD_MINE_RETRY_INTERVAL;
      }
    }

    if (!primaryHeld) mineRetryTimer = 0;

    if (secondaryHeld && repeatPlaceArmed) {
      placeRepeatTimer -= safeDelta;
      if (placeRepeatTimer <= 0) {
        const hit = readTargetHit(false);
        const placed = typeof tryRepeatPlaceAction === 'function'
          ? !!tryRepeatPlaceAction(hit)
          : !!tryPlaceActiveBlock?.(hit);
        placeRepeatTimer = placed ? HOLD_PLACE_REPEAT_INTERVAL : HOLD_PLACE_RETRY_INTERVAL;
      }
    } else {
      placeRepeatTimer = HOLD_PLACE_INITIAL_DELAY;
    }
  }

  function bind() {
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', handleWindowBlur);
  }

  function unbind() {
    document.removeEventListener('mousedown', handleMouseDown);
    document.removeEventListener('mouseup', handleMouseUp);
    window.removeEventListener('blur', handleWindowBlur);
    resetHeldState();
  }

  return {
    update,
    bind,
    unbind
  };
}
